import React from 'react';
import { WebsiteDesignProps } from './types';
import { Button } from "@/components/ui/button";
import { Search, Menu, Building2, Globe, BookOpen } from 'lucide-react';

const GeorgetownUniversity: React.FC<WebsiteDesignProps> = ({ previewData }) => {
    const schoolName = previewData?.name || "Georgetown University";

    return (
        <div className="min-h-screen bg-white font-serif text-[#041E42] selection:bg-[#63666A] selection:text-white">
            {/* Utility */}
            <div className="bg-[#f6f6f6] text-[#666] py-2 px-6 flex justify-between items-center text-xs font-sans uppercase tracking-wider">
                <div className="hidden md:block">Washington, DC</div>
                <div className="flex gap-6 w-full md:w-auto justify-end">
                    <a href="#" className="hover:text-[#041E42]">Ask {schoolName.split(' ')[0]}</a>
                    <a href="#" className="hover:text-[#041E42]">Directory</a>
                    <a href="#" className="hover:text-[#041E42]">Give</a>
                    <a href="#" className="hover:text-[#041E42]">MyAccess</a>
                </div>
            </div>

            {/* Nav */}
            <nav className="bg-white py-6 px-6 md:px-12 border-b border-gray-200">
                <div className="max-w-[1280px] mx-auto flex justify-between items-center">
                    <div className="flex flex-col">
                        <span className="font-serif text-3xl font-normal text-[#041E42] tracking-wide">{schoolName.split(' ')[0].toUpperCase()}</span>
                        <span className="font-serif text-sm font-normal text-[#63666A] tracking-[0.2em] uppercase">{schoolName.split(' ').slice(1).join(' ').toUpperCase() || "UNIVERSITY"}</span>
                    </div>

                    <div className="hidden lg:flex gap-10 font-sans font-bold text-[#041E42] text-xs uppercase tracking-widest">
                        <a href="#" className="hover:text-[#63666A]">Admissions</a>
                        <a href="#" className="hover:text-[#63666A]">Academics</a>
                        <a href="#" className="hover:text-[#63666A]">Research</a>
                        <a href="#" className="hover:text-[#63666A]">Campus Life</a>
                        <a href="#" className="hover:text-[#63666A]">About</a>
                    </div>

                     <div className="flex gap-4 items-center">
                         <span className="hidden md:block font-sans font-bold text-[#041E42] text-xs uppercase cursor-pointer hover:text-[#63666A]">School Websites</span>
                        <Search className="text-[#041E42] cursor-pointer" size={20} />
                        <Menu className="text-[#041E42] cursor-pointer lg:hidden" size={24} />
                    </div>
                </div>
            </nav>

            {/* Hero - Healy Hall / DC */}
            <header className="relative h-[650px]">
                <img 
                    src="https://images.unsplash.com/photo-1552033787-8d5b842df283?q=80&w=2074&auto=format&fit=crop" 
                    alt="Healy Hall" 
                    className="absolute inset-0 w-full h-full object-cover"
                />
                 {/* Blue overlay */}
                 <div className="absolute inset-0 bg-[#041E42]/20"></div>

                 <div className="absolute bottom-12 left-0 w-full px-8 md:px-12">
                     <div className="max-w-[1280px] mx-auto">
                        <div className="bg-white p-10 max-w-xl shadow-lg border-t-8 border-[#041E42]">
                            <h1 className="text-4xl md:text-5xl font-normal text-[#041E42] mb-6 leading-tight">
                                A leading research university in the nation's capital.
                            </h1>
                             <a href="#" className="inline-flex items-center gap-2 font-sans font-bold text-[#00274C] uppercase tracking-widest text-sm hover:underline">
                                 Read Our Story <span className="text-lg">→</span>
                             </a>
                        </div>
                     </div>
                 </div>
            </header>

            {/* Pillars / Values */}
            <section className="py-24 px-6 md:px-12 bg-white">
                <div className="max-w-[1280px] mx-auto grid grid-cols-1 md:grid-cols-3 gap-12 font-sans">
                     <div>
                         <Globe size={40} className="text-[#63666A] mb-6" />
                         <h3 className="text-xl font-bold text-[#041E42] mb-3 uppercase tracking-wide">Global Engagement</h3>
                         <p className="text-gray-600 leading-relaxed font-serif text-lg">
                             {schoolName.split(' ')[0]} prepares the next generation of global citizens to lead and make a difference in the world.
                         </p>
                     </div>
                     <div>
                         <Building2 size={40} className="text-[#63666A] mb-6" />
                         <h3 className="text-xl font-bold text-[#041E42] mb-3 uppercase tracking-wide">DC Location</h3>
                         <p className="text-gray-600 leading-relaxed font-serif text-lg">
                             Our location in Washington, DC, provides unparalleled opportunities for research, internships, and service.
                         </p>
                     </div>
                     <div>
                         <BookOpen size={40} className="text-[#63666A] mb-6" />
                         <h3 className="text-xl font-bold text-[#041E42] mb-3 uppercase tracking-wide">Jesuit Tradition</h3>
                         <p className="text-gray-600 leading-relaxed font-serif text-lg">
                             Founded on the principle of <em>cura personalis</em>, or care for the whole person.
                         </p>
                     </div>
                </div>
            </section>

             {/* News Blocks */}
             <section className="bg-[#f0f0f0] py-24 px-6 md:px-12">
                 <div className="max-w-[1280px] mx-auto">
                     <h2 className="text-3xl font-normal text-[#041E42] mb-12 border-b border-gray-300 pb-4">Latest News</h2>

                     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                         <div className="bg-white group cursor-pointer lg:col-span-2 flex flex-col md:flex-row h-auto md:h-80 shadow-sm">
                             <div className="md:w-1/2 h-64 md:h-full overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1577495508326-19a1b3f65205?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Politics"/>
                             </div>
                             <div className="p-8 md:w-1/2 flex flex-col justify-center">
                                 <span className="text-[#63666A] font-sans font-bold text-xs uppercase tracking-widest mb-3">Public Policy</span>
                                 <h3 className="text-2xl font-normal text-[#041E42] mb-4 group-hover:underline decoration-1 underline-offset-4">
                                     McCourt School releases new poll on voter sentiment
                                 </h3>
                                 <p className="text-gray-600 font-sans text-sm">Political experts analyze the latest trends ahead of the upcoming election.</p>
                             </div>
                         </div>
                         
                         <div className="bg-white group cursor-pointer flex flex-col h-auto md:h-80 shadow-sm">
                             <div className="h-48 overflow-hidden">
                                 <img src="https://images.unsplash.com/photo-1505751172876-fa1923c5c528?q=80&w=2070&auto=format&fit=crop" className="w-full h-full object-cover transition-transform group-hover:scale-105" alt="Medicine"/>
                             </div>
                             <div className="p-6 flex flex-col justify-center flex-1">
                                 <span className="text-[#63666A] font-sans font-bold text-xs uppercase tracking-widest mb-2">Health</span>
                                 <h3 className="text-lg font-normal text-[#041E42] group-hover:underline decoration-1 underline-offset-4">
                                     Advances in cancer immunotherapy at Lombardi Center
                                 </h3>
                             </div>
                         </div>
                     </div>
                 </div>
             </section>

             {/* Footer */}
             <footer className="bg-[#041E42] text-white py-16 px-6 md:px-12 border-t-8 border-[#63666A]">
                 <div className="max-w-[1280px] mx-auto flex flex-col justify-center items-center text-center">
                      <span className="font-serif text-3xl mb-6 block tracking-wider">{schoolName.toUpperCase()}</span>
                      <p className="font-sans text-gray-400 text-sm mb-10 max-w-md">
                          37th and O Streets, N.W. <br/>
                          Washington, D.C. 20057 <br/>
                          (202) 687-0100
                      </p>

                      <div className="w-full border-t border-[#63666A]/30 my-8"></div>
                      
                      <div className="flex flex-wrap justify-center gap-8 font-sans font-bold text-xs uppercase tracking-widest text-[#63666A]">
                          <a href="#" className="hover:text-white">Accessibility</a>
                          <a href="#" className="hover:text-white">Copyright</a>
                          <a href="#" className="hover:text-white">Privacy</a>
                          <a href="#" className="hover:text-white">Contact</a>
                      </div>
                 </div>
             </footer>
        </div>
    );
};

export default GeorgetownUniversity;
